import React, { useEffect, useState } from 'react';
import { FormElement, FormLabel, FormNumberInput, DragHandle } from "../components/StyledElements";
import { Handle, Position } from '@xyflow/react';
import { NodeCard } from './NodeCard';
import { CloseButton } from '../components/CloseButton';


const DEFAULT_QUERY = `SELECT id, media_id, text, additional_data
FROM media_chunks
WHERE id IN (:chunk_ids)`;


const stringifyParams = (params) => {
  if (params === undefined || params === null) {
    return "{}";
  }
  if (typeof params === "string") {
    return params;
  }
  try {
    return JSON.stringify(params, null, 2);
  } catch (error) {
    return "{}";
  }
};


export const SQLQueryNodeCard = ({ onAdd = () => { }, style = {} }) => {
  return (
    <NodeCard
      title="SQL Query"
      description="Run a read-only, parameterized SQL query"
      onAdd={() => onAdd(500)}
      style={style}
    />
  )
};


export const SQLQueryNode = ({ id, data }) => {
  const [localData, setLocalData] = useState({
    query: DEFAULT_QUERY,
    params: "{}",
    result: "objects",
    maxRows: 100,
    timeoutMs: 5000,
  });
  const [paramsError, setParamsError] = useState(null);

  useEffect(() => {
    if (!data) {
      return;
    }
    setLocalData({
      query: data.query || DEFAULT_QUERY,
      params: stringifyParams(data.params),
      result: data.result || "objects",
      maxRows: data.maxRows || 100,
      timeoutMs: data.timeoutMs || 5000,
    });
  }, [data]);

  const update = (patch) => {
    setLocalData((current) => ({ ...current, ...patch }));
    data.onUpdateData(id, patch);
  };

  const updateParams = (text) => {
    setLocalData((current) => ({ ...current, params: text }));
    setParamsError(null);

    if (!text.trim()) {
      data.onUpdateData(id, { params: {} });
      return;
    }

    try {
      data.onUpdateData(id, { params: JSON.parse(text) });
    } catch (error) {
      setParamsError(error.message);
      data.onUpdateData(id, { params: text });
    }
  };

  const fieldStyle = {
    width: "100%",
    padding: "8px",
    borderRadius: "6px",
    border: "1px solid var(--main-border-color)",
    backgroundColor: "var(--content-item-bg)",
    color: "var(--main-font-color)",
  };

  const codeStyle = {
    ...fieldStyle,
    fontFamily: "monospace",
    fontSize: "12px",
    lineHeight: 1.45,
    resize: "vertical",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "10px", border: "1px solid var(--main-border-color)", borderRadius: "8px", backgroundColor: "var(--content-item-bg)" }}>
      <CloseButton onClick={() => data.onDelete(id)} />
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <DragHandle />
        <FormLabel label="SQL Query" />

        <FormElement>
          <FormLabel label="Query (read-only SELECT/WITH)" />
          <textarea
            value={localData.query}
            onChange={(e) => update({ query: e.target.value })}
            rows={6}
            spellCheck={false}
            style={codeStyle}
            placeholder="SELECT id, media_id, text FROM media_chunks WHERE id IN (:chunk_ids)"
          />
        </FormElement>

        <FormElement>
          <FormLabel label="Params (JSON)" />
          <textarea
            value={localData.params}
            onChange={(e) => updateParams(e.target.value)}
            rows={5}
            spellCheck={false}
            style={codeStyle}
            placeholder={'{\n  "chunk_ids": "{input.document_ids}"\n}'}
          />
          <div style={{ fontSize: "12px", opacity: 0.75, lineHeight: 1.4 }}>
            Use <code>:name</code> binds in the query. Values may reference{" "}
            <code>{"{input}"}</code>, <code>{"{input.key}"}</code>,{" "}
            <code>{"{context.key}"}</code>, or a bare <code>{"{key}"}</code>.
            Values are bound by the server, never interpolated into SQL.
          </div>
          {paramsError && (
            <div style={{ fontSize: "12px", color: "#c0392b", lineHeight: 1.4 }}>
              Invalid JSON: {paramsError}
            </div>
          )}
        </FormElement>

        <FormElement>
          <FormLabel label="Result shape" />
          <select
            value={localData.result}
            onChange={(e) => update({ result: e.target.value })}
            style={fieldStyle}
          >
            <option value="objects">Objects (column name → value)</option>
            <option value="rows">Rows (arrays in column order)</option>
            <option value="first">First row only</option>
          </select>
        </FormElement>

        <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
          <FormElement style={{ width: "50%" }}>
            <FormLabel label="Max rows" />
            <FormNumberInput
              value={localData.maxRows}
              onChange={(e) => update({ maxRows: Number(e.target.value) })}
              min={1}
              max={1000}
              step={1}
            />
          </FormElement>
          <FormElement style={{ width: "50%" }}>
            <FormLabel label="Timeout (ms)" />
            <FormNumberInput
              value={localData.timeoutMs}
              onChange={(e) => update({ timeoutMs: Number(e.target.value) })}
              min={100}
              max={60000}
              step={100}
            />
          </FormElement>
        </div>

        <div style={{ fontSize: "12px", opacity: 0.75, lineHeight: 1.4 }}>
          Only one read-only SELECT/WITH statement is allowed. Comments,
          multi-statements, DML/DDL, locks, and SELECT INTO OUTFILE are rejected.
          {localData.timeoutMs > 0 && (
            <> On MySQL/MariaDB the timeout is applied as an optimizer hint.</>
          )}
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Left}
        style={{ top: "110px", height: "12px", width: "12px" }}
        id="sql-query-input"
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ height: "12px", width: "12px" }}
        id="sql-query-output"
      />
    </div>
  );
};

export default SQLQueryNode;
